// src/components/thread/ThreadList.jsx
import React, { useState, useEffect, useRef } from 'react';
import { UserCircleDisplay } from '../../utils/UserCustomDisplay';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import PaginationControls from '../../utils/PaginationControls';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';
import { useTimezone } from '../../utils/TimezoneContext';

const SLRProjectFilter = ({
  slrProjects,     
  selectedSlrProjects, 
  onApply, 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState(selectedSlrProjects);
  const [searchInput, setSearchInput] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    setTempSelected(selectedSlrProjects);
  }, [selectedSlrProjects]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchInput('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (projectId) => {
    setTempSelected((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleApply = () => {
    onApply(tempSelected);
    setIsOpen(false);
    setSearchInput('');
  };

  const handleClearAll = () => {
    setTempSelected([]);
    onApply([]);
    setIsOpen(false);
    setSearchInput('');
  };

  const filteredProjects = slrProjects.filter((p) =>
    p.name.toLowerCase().includes(searchInput.toLowerCase())
  );

  return (
    <div className="position-relative ms-2" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        type="button"
        className="ev-message-card2 ev-btn-small ev-bold-reg py-1 px-2"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <i className="bi bi-funnel me-1"></i>Filter by Projects
        {selectedSlrProjects.length > 0 && ` (${selectedSlrProjects.length})`}
        <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-1`}></i>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="ev-message-card2 d-block ev-card-3 position-absolute z-1000 mt-1 min-w-300 py-2 px-3">

          {/* Search input */}
          <div className="pb-1 ">
            <input
              type="text"
              className="form-control search-input ms-0 ps-3 ev-small-p-blue"
              placeholder="Search projects..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoFocus
            />
          </div>

          {/* Project list */}
          <div className="scrollable-element vh-25">
            {slrProjects.length === 0 ? (
              <div className="ev-padding-small d-flex justify-content-center align-items-center h-100 text-align-center">
                No SLR projects available
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="ev-padding-small d-flex justify-content-center align-items-center h-100 text-align-center">
                No projects match your search
              </div>
            ) : (
              filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className="ev-padding-small cursor-pointer hover-bg-light d-flex align-items-center"
                  onClick={() => handleToggle(project.id)}
                >
                  <input
                    type="checkbox"
                    checked={tempSelected.includes(project.id)}
                    onChange={() => {}}
                    className="me-2"
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="ev-xsmall-p-dark">{project.name}</span>
                </div>
              ))
            )}
          </div>

          {/* Action buttons */}
          <div className="d-flex justify-content-end gap-1 mt-1 pt-1">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small"
              onClick={handleClearAll}
            >
              Clear All
            </button>
            <button
              type="button"
              className="ev-btn-blue-fill-round ev-bold-reg ev-btn-small ms-1"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>

        </div>
      )}
    </div>
  );
};

const UnstructuredThreadList = ({ 
  threads,
  total,
  page,
  setPage,
  size,
  setSize,
  search,
  setSearch,
  filterType,
  setFilterType,
  slrProjects,
  selectedSlrProjects,
  setSelectedSlrProjects,
  onRefresh,
  onEditThread,
  onViewThreadDetails,
}) => {
  const [expandedTagThreadId, setExpandedTagThreadId] = useState(null);
  
  // Delete thread states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);
  
  // Search input state (separate from the actual search term)
  const [searchInput, setSearchInput] = useState(search);
  
  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();
  const { formatDateTime } = useTimezone();

  // API base URL
  const API_BASE_URL = '/pceapi/threads/unstructured';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };

  // Function to toggle tag expansion for a specific thread
  const toggleTagExpansion = (threadId) => {
    if (expandedTagThreadId === threadId) {
      setExpandedTagThreadId(null); // Collapse if already expanded
    } else {
      setExpandedTagThreadId(threadId); // Expand the clicked thread's tags
    }
  };
  
  // Show delete confirmation dialog
  const confirmDelete = (thread) => {
    setThreadToDelete(thread);
    setShowDeleteConfirm(true);
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  // Close success popup
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    onRefresh();
  };
  
  // Handle thread deletion
  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    
    if (!threadToDelete) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting Data Collection "${threadToDelete.name}"`);
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete-thread`,
        { 
          headers,
          data: {
            thread_id: threadToDelete.id
          }
        }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to delete Data Collection');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(data.message || 'Data Collection deleted successfully');
      setIsSuccessPopupVisible(true);
      
    } catch (error) {
      console.error('Error deleting Data Collection:', error);
      setPopupErrorMessage(error.message || 'Failed to delete Data Collection');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setThreadToDelete(null);
    }
  };
  
  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Generate body content for delete modal
  const getDeleteModalBodyContent = () => {
    if (!threadToDelete) return null;
    
    return (
      <div>
        <p className="mb-0">This will permanently delete:</p>
        <ul>
          <li>All {threadToDelete.document_count} documents in this Data Collection</li>
          <li>All data and files in S3 storage</li>
          <li>All associated metadata and processing results</li>
        </ul>
        <p className="text-danger mb-0">This action cannot be undone.</p>
      </div>
    );
  };
  
  return (
    <div className="ev-card-4 w-100 ev-padding-large">
      <div className="row mt-1 mb-0">
        <div className="col-4 position-relative">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search by Data Collection name"
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyPress={handleSearchKeyPress}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        
        <div className="col-8 text-end d-flex justify-content-end align-items-center flex-wrap gap-2">
          <SLRProjectFilter
            slrProjects={slrProjects}
            selectedSlrProjects={selectedSlrProjects}
            onApply={setSelectedSlrProjects}
          />
          <div className="btn-group custom-btn-group-outer">
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'ALL' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('ALL')}
            >
              All
            </button>
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'PUBLIC' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('PUBLIC')}
            >
              Public
            </button>
            <button 
              className={`btn custom-btn-group-inner ${filterType === 'PRIVATE' ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
              onClick={() => setFilterType('PRIVATE')}
            >
              Private
            </button>
          </div>
          <button 
            className="ev-btn-lightblue-fill-round ev-bold-reg ms-1" 
            onClick={onRefresh}
          >
            <i className="bi bi-arrow-repeat"></i> Refresh 
          </button>
        </div>
      </div>
      
      <div className="table-container">
        <table className="table-custom1 mt-0 overflow-hidden w-100">
          <thead>
            <tr>
              <th className="w-25">
                <label>Data Collection Name</label>
              </th>
              <th className="w-20 text-center">
                <label>Owner</label>
              </th>
              <th className="w-5 text-center">
                <label>Files</label>
              </th>
              <th className="w-10 text-center">
                <label>Access</label>
              </th>
              <th className="w-15 text-center">
                <label>Private Access</label>
              </th>
              <th className="w-15 text-center">
                <label>Created</label>
              </th>
              <th className="w-10">
                <label>Actions</label>
              </th>
            </tr>
          </thead>
        </table>

        <div className="scrollable-element dashboard-right-contentarea-table-height-1">
          <table className="table-custom1 overflow-hidden w-100">
            <tbody>
              {threads.length === 0 ? (
                <div className="text-center py-5">
                    <p className="ev-small-p-blue">No Data Collections found</p>
                </div>
              ) : (
                threads.map((thread) => (
                  <tr key={thread.id}>
                    <td className="align-top w-25">
                      <div className="ev-reg-p-blue ev-bold-large mb-0 avatar-tooltip mb-1" data-tooltip={thread.name || 'None'}>
                          &nbsp;
                          <TrimContent text={thread.name} maxLength={25} />
                        </div>
                      {thread.tags && thread.tags.length > 0 && (
                        <div className="position-relative">
                          <div className="d-flex flex-wrap gap-1">
                            {/* Show first 2 tags or all tags if expanded */}
                            {(expandedTagThreadId === thread.id 
                              ? thread.tags 
                              : thread.tags.slice(0, 2)
                            ).map((tag, idx) => (
                              <span key={idx} className="ev-message-card1 ev-fs-10">{tag}</span>
                            ))}
                            
                            {/* Show +more button if not expanded and more than 2 tags */}
                            {thread.tags.length > 2 && expandedTagThreadId !== thread.id && (
                              <span 
                                className="more-circle more-circle--small ev-btn-blue-outline-round  ev-bold-reg" 
                                onClick={() => toggleTagExpansion(thread.id)}
                                data-tooltip="Show more tags"
                              >
                                +{thread.tags.length - 2}
                              </span>
                            )}
                            
                            {/* Show collapse button if expanded */}
                            {expandedTagThreadId === thread.id && (
                              <span 
                                className="more-circle more-circle--small ev-btn-blue-outline-round  ev-bold-reg" 
                                onClick={() => toggleTagExpansion(thread.id)}
                                data-tooltip="Hide tags"
                              >
                                <i className="bi bi-dash"></i>
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="align-top w-20 text-center avatar-tooltip" data-tooltip={thread.created_by_email || 'None'}>
                      <div className="ev-message-card5 ev-fs-11 d-inline-block">
                        <TrimContent text={thread.created_by_email} maxLength={20} />
                      </div>
                    </td>
                    <td className="align-top w-5 text-center">{thread.document_count}</td>
                    <td className="align-top w-10 text-center">
                      <span className={`badge badge-sm ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary w-100' : 'bg-danger w-100'}`}>
                        {thread.thread_access_level}
                      </span>
                    </td>
                    <td className="align-top w-15">
                      <div className="d-flex justify-content-center">
                        {thread.thread_access_level === 'PRIVATE' && thread.user_access && thread.user_access.length > 0 ? (
                          <UserCircleDisplay users={thread.user_access} maxDisplay={4} />
                        ) : thread.thread_access_level === 'PUBLIC' ? (
                          <span className="ev-small-p-dark">all users</span>
                        ) : (
                          <span className="ev-small-p-dark">None</span>
                        )}
                      </div>
                    </td>
                    <td className="align-top w-15 text-center">{formatDateTime(thread.created_at)}</td>
                    <td className="align-top w-10">
                      <div className="btn-group" role="group">
                        <button 
                          className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2 avatar-tooltip" 
                          onClick={() => confirmDelete(thread)}
                          data-tooltip="Delete Data Collection"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                        <button 
                          className="btn ev-btn-skyblue-fill-round ev-btn-med  ev-radius-xsmall pe-2 ps-2 avatar-tooltip" 
                          onClick={() => onEditThread(thread)}
                          data-tooltip="Edit Data Collection"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button 
                          className="btn ev-btn-lightblue-fill-round ev-btn-med  ev-radius-xsmall pe-2 ps-2 avatar-tooltip" 
                          onClick={() => onViewThreadDetails(thread.id)}
                          data-tooltip="View Details"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="ev-small-p-dark">
          Showing {threads.length} of {total} entries
        </div>
        <div className="d-flex gap-2">
          <button 
            className="ev-btn-light-fill" 
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            <i className="bi bi-chevron-left"></i> 
          </button>
          <button className="ev-btn-light-fill" disabled>
            Page {page} of {Math.ceil(total / size)}
          </button>
          <button 
            className="ev-btn-light-fill" 
            disabled={page >= Math.ceil(total / size)}
            onClick={() => setPage(page + 1)}
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <div>
          <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
        </div>
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        objectName="Data Collection"
        itemName={threadToDelete?.name}
        bodyContent={getDeleteModalBodyContent()}
      />
    </div>
  );
};

export default UnstructuredThreadList;